// =============================================================================
// CardRegisterScreen — /ap/cards (T-493).
//
// The company cards, and which Xero bank account each one's spend reconciles
// into. This screen exists because that mapping used to live only in a database
// migration: a new company card meant a developer, which is exactly the kind of
// dependency this platform is meant to remove.
//
// The card_type choice is the important one and it is not cosmetic:
//   company_credit / company_debit -> Spend Money against that bank account
//   employee_personal              -> a Bill payable TO the employee
// Getting it wrong either leaves a crew member out of pocket or posts a
// personal card purchase against a company account.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Plus, Check, X, AlertTriangle, Archive, RotateCcw } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Spinner, EmptyState, Field, Input, Select, Pill } from "@/components/ui";

type CardRow = {
  card_id: string;
  last4: string;
  label: string | null;
  card_type: "company_credit" | "company_debit" | "employee_personal";
  holder_employee_id: string | null;
  xero_bank_account_code: string | null;
  xero_account_name: string | null;
  is_active: boolean;
  notes: string | null;
};

const CARD_TYPES = [
  { value: "company_credit",    label: "Company credit card", blurb: "Spend money against the card's bank account" },
  { value: "company_debit",     label: "Company debit card",  blurb: "Spend money against the card's bank account" },
  { value: "employee_personal", label: "Employee personal",   blurb: "Bill payable to the employee — reimbursement" },
];

const blankDraft = (): Partial<CardRow> => ({
  last4: "", label: "", card_type: "company_credit",
  holder_employee_id: null, xero_bank_account_code: null, notes: "", is_active: true,
});

export default function CardRegisterScreen() {
  useRequireAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft]     = useState<Partial<CardRow>>(blankDraft());
  const [adding, setAdding]   = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data: cards = [], isLoading } = useQuery({
    queryKey: ["card_register_admin"],
    queryFn: async () => {
      const { data, error } = await supabase.from("card_register")
        .select("card_id, last4, label, card_type, holder_employee_id, xero_bank_account_code, xero_account_name, is_active, notes")
        .order("is_active", { ascending: false }).order("last4");
      if (error) throw error;
      return (data ?? []) as CardRow[];
    },
  });

  // Only Bank-type accounts can hold a spend. Anything else is rejected by the
  // trigger anyway, so do not offer it.
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ["xero_bank_accounts"],
    queryFn: async () => {
      const { data } = await supabase.from("xero_accounts")
        .select("code, name, account_type").eq("active", true).ilike("account_type", "bank").order("code");
      return (data ?? []) as any[];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["card_register_employees"],
    queryFn: async () => {
      const { data } = await supabase.from("employees")
        .select("employee_id, first_name, last_name").eq("is_active", true)
        .order("first_name");
      return (data ?? []) as any[];
    },
  });

  const save = useMutation({
    mutationFn: async (row: Partial<CardRow>) => {
      const last4 = String(row.last4 ?? "").trim();
      if (!/^\d{4}$/.test(last4)) throw new Error("Last 4 must be exactly four digits.");
      const isPersonal = row.card_type === "employee_personal";
      if (isPersonal && !row.holder_employee_id) {
        throw new Error("A personal card needs a cardholder — the reimbursement is paid to them.");
      }
      if (!isPersonal && !row.xero_bank_account_code) {
        throw new Error("A company card needs a Xero bank account, or its spend cannot be reconciled.");
      }
      const patch = {
        last4,
        label:                  (row.label ?? "").trim() || null,
        card_type:              row.card_type,
        holder_employee_id:     row.holder_employee_id || null,
        // A personal card is never a spend against a company account.
        xero_bank_account_code: isPersonal ? null : (row.xero_bank_account_code || null),
        notes:                  (row.notes ?? "").trim() || null,
        is_active:              row.is_active ?? true,
      };
      const { error } = row.card_id
        ? await supabase.from("card_register").update(patch).eq("card_id", row.card_id)
        : await supabase.from("card_register").insert(patch);
      if (error) throw error;
    },
    onSuccess: () => {
      setEditing(null); setAdding(false); setDraft(blankDraft()); setErr(null);
      qc.invalidateQueries({ queryKey: ["card_register_admin"] });
      qc.invalidateQueries({ queryKey: ["ap_card_register"] });
    },
    onError: (e: any) => setErr(e?.message ?? "Could not save."),
  });

  // Retire rather than delete — an archived card still has to explain historical
  // spend, and the unique index only applies to active cards so the number can
  // be reissued later.
  const setActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("card_register").update({ is_active: active }).eq("card_id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["card_register_admin"] });
      qc.invalidateQueries({ queryKey: ["ap_card_register"] });
    },
    onError: (e: any) => setErr(e?.message ?? "Could not update."),
  });

  const empName = (id: string | null) => {
    const e = (employees as any[]).find((x) => x.employee_id === id);
    return e ? [e.first_name, e.last_name].filter(Boolean).join(" ") : null;
  };
  const visible = useMemo(
    () => (cards as CardRow[]).filter((c) => showArchived || c.is_active),
    [cards, showArchived]);
  const blocked = (cards as CardRow[]).filter(
    (c) => c.is_active && c.card_type !== "employee_personal" && !c.xero_bank_account_code);

  function startEdit(c: CardRow) { setErr(null); setAdding(false); setEditing(c.card_id); setDraft({ ...c }); }
  function startAdd()            { setErr(null); setEditing(null); setAdding(true);  setDraft(blankDraft()); }
  function cancel()              { setErr(null); setEditing(null); setAdding(false); setDraft(blankDraft()); }

  const isPersonal = draft.card_type === "employee_personal";

  const form = (
    <div className="bg-ink-raised border border-line rounded-lg p-4 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Last 4 digits" hint="How a receipt is matched back to this card">
          <Input value={draft.last4 ?? ""} mono maxLength={4} placeholder="9138"
            onChange={(e) => setDraft((d) => ({ ...d, last4: e.target.value.replace(/\D/g, "").slice(0, 4) }))} />
        </Field>
        <Field label="Label" hint="Who carries it / what it is for">
          <Input value={draft.label ?? ""} placeholder="Clint Bidgood — company credit"
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} />
        </Field>
        <Field label="Card type" hint={CARD_TYPES.find((t) => t.value === draft.card_type)?.blurb}>
          <Select value={draft.card_type ?? "company_credit"}
            onChange={(e) => setDraft((d) => ({ ...d, card_type: e.target.value as any }))}>
            {CARD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </Field>
        <Field label="Cardholder" hint={isPersonal ? "Required — the reimbursement is paid to this person" : "Optional"}>
          <Select value={draft.holder_employee_id ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, holder_employee_id: e.target.value || null }))}>
            <option value="">— none —</option>
            {(employees as any[]).map((e) => (
              <option key={e.employee_id} value={e.employee_id}>
                {[e.first_name, e.last_name].filter(Boolean).join(" ")}
              </option>
            ))}
          </Select>
        </Field>
        {!isPersonal && (
          <Field label="Xero bank account" hint="Where this card's spend reconciles. Without it, a push is blocked.">
            <Select value={draft.xero_bank_account_code ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, xero_bank_account_code: e.target.value || null }))}>
              <option value="">— pick an account —</option>
              {(bankAccounts as any[]).map((a) => (
                <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Notes">
          <Input value={draft.notes ?? ""} placeholder="optional"
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
        </Field>
      </div>

      {isPersonal && (
        <div className="mt-3 flex items-start gap-2 rounded border border-line bg-ink px-3 py-2 text-[12px] text-text-muted">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
          <div>A personal card posts as a <strong>bill payable to the cardholder</strong>, not a
            company spend. No bank account applies — the money is owed to them.</div>
        </div>
      )}

      {err && (
        <div className="mt-3 flex items-start gap-2 rounded border border-danger/30 bg-danger/10 px-3 py-2 text-[12px] text-danger">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /><div>{err}</div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button variant="secondary" icon={X} onClick={cancel}>Cancel</Button>
        <Button icon={Check} disabled={save.isPending} onClick={() => save.mutate(draft)}>
          {save.isPending ? "Saving…" : "Save card"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link to="/ap" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text">
        <ArrowLeft size={14} /> AP / Spend
      </Link>

      <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-text">Card register</h1>
          <p className="text-[13px] text-text-muted mt-1">
            Company cards and the Xero bank account each one reconciles into. A receipt's last 4
            digits are matched here to decide how the spend is posted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Hide retired" : "Show retired"}
          </Button>
          <Button icon={Plus} onClick={startAdd}>Add card</Button>
        </div>
      </div>

      {blocked.length > 0 && (
        <div className="mt-4 flex items-start gap-2 rounded border border-warn/30 bg-warn/10 px-3 py-2 text-[12px] text-warn">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {blocked.length === 1 ? "One card has" : `${blocked.length} cards have`} no Xero bank account
            </div>
            <div className="mt-0.5">
              Spend on {blocked.map((c) => `••••${c.last4}`).join(", ")} will be blocked at push
              until an account is set.
            </div>
          </div>
        </div>
      )}

      {adding && form}

      {isLoading ? (
        <div className="mt-6 flex justify-center"><Spinner /></div>
      ) : visible.length === 0 ? (
        <div className="mt-6">
          <EmptyState icon={CreditCard} title="No cards yet"
            description="Add the company cards so receipts can be matched to them." />
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {visible.map((c) => (
            <div key={c.card_id}
              className={`bg-ink-card border rounded-lg p-3 ${c.is_active ? "border-line" : "border-line opacity-60"}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <CreditCard size={16} className="text-text-muted shrink-0" />
                <span className="font-mono text-[13px] text-text">••••{c.last4}</span>
                <span className="text-[13px] text-text">{c.label ?? "—"}</span>
                <Pill small
                  label={CARD_TYPES.find((t) => t.value === c.card_type)?.label ?? c.card_type}
                  tone={c.card_type === "employee_personal" ? "warning" : "default"} />
                {c.card_type !== "employee_personal" && (
                  c.xero_bank_account_code
                    ? <span className="text-[12px] text-text-muted font-mono">
                        → {c.xero_bank_account_code} {c.xero_account_name ?? ""}
                      </span>
                    : <Pill small label="No bank account" tone="warning" />
                )}
                {c.holder_employee_id && (
                  <span className="text-[12px] text-text-muted">{empName(c.holder_employee_id) ?? ""}</span>
                )}
                {!c.is_active && <Pill small label="Retired" tone="default" />}
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(c)}>Edit</Button>
                  {c.is_active ? (
                    <Button variant="ghost" size="sm" icon={Archive}
                      title="Retire — keeps the history, frees the number for reissue"
                      onClick={() => setActive.mutate({ id: c.card_id, active: false })}>Retire</Button>
                  ) : (
                    <Button variant="ghost" size="sm" icon={RotateCcw}
                      onClick={() => setActive.mutate({ id: c.card_id, active: true })}>Restore</Button>
                  )}
                </div>
              </div>
              {c.notes && <div className="mt-1.5 text-[12px] text-text-muted">{c.notes}</div>}
              {editing === c.card_id && form}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
